import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useCreateUser } from '../contexts/users';
import { CREATE_STEPS, EMPTY_VALUES, toCreatePayload, validate } from '../utils/form';
import FormSteps, { errorsForStep } from '../../../common/components/form-steps/form-steps';
import UserFields from './fields';

import '../../clients/styles/form.css';

export default function CreateUserForm() {
    const navigate = useNavigate();
    const createUser = useCreateUser();

    const [values, setValues] = useState(EMPTY_VALUES);
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(0);

    const isLast = step === CREATE_STEPS.length - 1;

    // Forward validates ONLY this step's fields: an error has to appear next
    // to the field that caused it, not three screens later. Backward validates
    // nothing — nothing has been sent yet.
    const next = () => {
        const found = errorsForStep(validate(values, { withPassword: true }), CREATE_STEPS[step].fields);
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }
        setStep(s => s + 1);
    };

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Enter on any earlier step advances instead of sending: a form with a
        // single visible button teaches the keyboard the wrong thing.
        if (!isLast) {
            next();
            return;
        }

        const found = validate(values, { withPassword: true });
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            // Creating DOES carry the role: POST accepts it and there is no
            // separate route for it. Editing is the one that splits, because
            // that is where the backend split it.
            const created = await createUser.mutateAsync(toCreatePayload(values));
            toast.success('Usuario creado');
            navigate(`/admin/users/${created.id}`, { replace: true });
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo crear el usuario');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">Nueva persona</h2>
                <p className="client-form__subtitle">
                    Una cuenta para entrar al panel. El rol decide qué puede hacer;
                    el correo es con lo que inicia sesión.
                </p>
            </header>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <FormSteps steps={CREATE_STEPS} current={step} onStep={setStep} />

                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos de la cuenta</h3>
                        <p className="client-form__section-text">
                            Quién es y con qué entra.
                        </p>
                    </div>

                    <UserFields
                        visible={CREATE_STEPS[step].fields}
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={createUser.isPending}
                        withPassword
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/people/usuarios" className="client-form__cancel">Cancelar</Link>
                    {step > 0 && (
                        <button
                            type="button"
                            className="client-form__back"
                            onClick={() => setStep(s => s - 1)}
                            disabled={createUser.isPending}
                        >
                            Atrás
                        </button>
                    )}
                    {!isLast
                        ? (
                            <button type="button" className="client-form__submit" onClick={next}>
                                Siguiente
                            </button>
                        )
                        : (
                            <button
                                type="submit"
                                className="client-form__submit"
                                disabled={createUser.isPending}
                            >
                                {createUser.isPending ? 'Guardando...' : 'Crear usuario'}
                            </button>
                        )}
                </div>
            </form>
        </section>
    );
}
